import React, { useState, useEffect } from "react";
import {
  ShoppingCart,
  Menu,
  X,
  Plus,
  Minus,
  Trash2,
  Home,
  Info,
  Mail,
  ShoppingBag,
  FileText,
  Package,
  ChevronDown,
  Star,
  Truck,
  Shield,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useCartContext } from "../context/CartContext";

// Simple Shop Now Button Component
const SimpleShopButton = () => {
  return (
    <Link to="/devis">
      <button className="group relative bg-gradient-to-r from-emerald-500 to-green-600 hover:from-green-600 hover:to-emerald-500 text-white px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 overflow-hidden">
        {/* Shimmer effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-700"></div>

                  <div className="flex items-center space-x-2 relative z-10">
          <ShoppingBag className="w-4 h-4" />
          <span className="font-semibold text-sm">Shop Now</span>
                    </div>

        {/* Pulse ring effect */}
        <div className="absolute inset-0 rounded-full border-2 border-white opacity-0 group-hover:opacity-50 group-hover:animate-ping"></div>
      </button>
                </Link>
  );
};

// Main Navbar Component
export const Navbar = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity } = useCartContext();

  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const proceedAllToDevis = () => {
    const uniqueSizes = [
      ...new Set(cartItems.map((item) => item.selectedSize?.name)),
    ];
    const devisProduct = {
      id: "multiple",
      name: `Panier (${cartItems.length} articles)`,
      quantity: cartItems.reduce((sum, item) => sum + item.quantity, 0),
      sizes: uniqueSizes,
      items: cartItems,
    };
    localStorage.setItem("devisProduct", JSON.stringify(devisProduct));
    navigate("/devis");
  };

  const handleCheckout = () => {
    setIsCartOpen(false);
    proceedAllToDevis();
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      <nav className={`transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-100' 
          : 'bg-gradient-to-r from-green-50 via-emerald-50 to-green-100 shadow-sm'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="group">
                <div className="flex items-center space-x-3">
                  <div className="relative">
                <img
                      className="w-24 h-24 object-contain transition-transform duration-300 group-hover:scale-110"
                      src="/Green_Eco_Bags_Logo-removebg-preview.png"
                  alt="Green Eco Logo"
                />
                    <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full opacity-0 group-hover:opacity-20 blur-sm transition-opacity duration-300"></div>
                  </div>
                  <div className="hidden sm:block">
                    <h1 className="text-lg font-bold text-gray-800 group-hover:text-green-600 transition-colors duration-300">
                      Green Eco Bags
                    </h1>
                    <p className="text-sm text-gray-500">Sustainable Solutions</p>
                  </div>
                </div>
              </Link>
            </div>

            {/* Mobile Menu */}
            <div className="flex items-center space-x-2 md:hidden">
              {/* Shop Button for Mobile */}
              <SimpleShopButton />

              {/* Cart Button */}
              <div className="relative">
                <button
                  onClick={() => setIsCartOpen(!isCartOpen)}
                  className="relative p-2 rounded-full hover:bg-gray-100 transition-colors duration-200 group"
                >
                  <ShoppingCart className="w-6 h-6 text-gray-600 group-hover:text-green-600 transition-colors duration-200" />
                  {totalItems > 0 && (
                    <span className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shadow-lg animate-pulse">
                      {totalItems}
                    </span>
                  )}
                </button>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6 text-gray-600" />
                ) : (
                  <Menu className="w-6 h-6 text-gray-600" />
                )}
              </button>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              {/* Navigation Links */}
              <div className="flex items-center space-x-6">
                <Link 
                  to="/" 
                  className="text-gray-700 hover:text-green-600 font-medium transition-colors duration-200 relative group"
                >
                  Home
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-green-500 to-emerald-500 group-hover:w-full transition-all duration-300"></div>
              </Link>
                <Link 
                  to="/about" 
                  className="text-gray-700 hover:text-green-600 font-medium transition-colors duration-200 relative group"
                >
                About
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-green-500 to-emerald-500 group-hover:w-full transition-all duration-300"></div>
              </Link>
              <Link
                to="/contact"
                  className="text-gray-700 hover:text-green-600 font-medium transition-colors duration-200 relative group"
              >
                Contact
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-green-500 to-emerald-500 group-hover:w-full transition-all duration-300"></div>
              </Link>
              </div>

              {/* Shop Button for Desktop */}
              <SimpleShopButton />

              {/* Cart Button */}
              <div className="relative">
                <button
                  onClick={() => setIsCartOpen(!isCartOpen)}
                  className="relative p-2 rounded-full hover:bg-gray-100 transition-all duration-200 group"
                >
                  <ShoppingCart className="w-6 h-6 text-gray-600 group-hover:text-green-600 transition-colors duration-200" />
                  {totalItems > 0 && (
                    <span className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shadow-lg animate-pulse">
                      {totalItems}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Menu */}
          <div
            className={`md:hidden fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${
              isMobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
            onClick={closeMobileMenu}
          >
            <div
              className={`fixed inset-y-0 left-0 w-80 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out ${
                isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
                }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-emerald-500 to-green-600 text-white p-6 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-10 animate-pulse"></div>
                <div className="flex justify-between items-center relative z-10">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                      <Star className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Green Eco Bags</h3>
                      <p className="text-sm opacity-90">Sustainable Solutions</p>
                    </div>
                  </div>
                  <button 
                    onClick={closeMobileMenu} 
                    className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-all duration-200"
                  >
                    <X className="w-5 h-5" />
                </button>
              </div>
              </div>

              {/* Navigation Links */}
              <div className="p-4 space-y-2">
                <Link
                  to="/"
                  className="flex items-center space-x-4 text-gray-700 hover:text-green-600 hover:bg-green-50 p-3 rounded-xl transition-all duration-200 group"
                  onClick={closeMobileMenu}
                >
                  <div className="w-10 h-10 bg-gray-100 group-hover:bg-green-100 rounded-lg flex items-center justify-center transition-colors duration-200">
                  <Home className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="font-medium">Home</span>
                    <p className="text-xs text-gray-500">Welcome page</p>
                  </div>
                </Link>
                
                <Link
                  to="/about"
                  className="flex items-center space-x-4 text-gray-700 hover:text-green-600 hover:bg-green-50 p-3 rounded-xl transition-all duration-200 group"
                  onClick={closeMobileMenu}
                >
                  <div className="w-10 h-10 bg-gray-100 group-hover:bg-green-100 rounded-lg flex items-center justify-center transition-colors duration-200">
                  <Info className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="font-medium">About</span>
                    <p className="text-xs text-gray-500">Our story</p>
                  </div>
                </Link>
                
                <Link
                  to="/contact"
                  className="flex items-center space-x-4 text-gray-700 hover:text-green-600 hover:bg-green-50 p-3 rounded-xl transition-all duration-200 group"
                  onClick={closeMobileMenu}
                >
                  <div className="w-10 h-10 bg-gray-100 group-hover:bg-green-100 rounded-lg flex items-center justify-center transition-colors duration-200">
                  <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="font-medium">Contact</span>
                    <p className="text-xs text-gray-500">Get in touch</p>
                  </div>
                </Link>
              </div>

              {/* Footer */}
              <div className="absolute bottom-0 left-0 right-0 bg-gray-50 p-4 border-t border-gray-100">
                <div className="flex items-center justify-center space-x-4 text-xs text-gray-600">
                  <div className="flex items-center space-x-1">
                    <Truck className="w-3 h-3 text-green-500" />
                    <span>Fast Delivery</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Shield className="w-3 h-3 text-green-500" />
                    <span>Quality Guaranteed</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cart Dropdown */}
          {isCartOpen && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-black bg-opacity-25 z-40"
                onClick={() => setIsCartOpen(false)}
              />
              
              {/* Cart Panel */}
              <div className="absolute right-4 mt-2 w-96 bg-white shadow-2xl rounded-2xl border border-gray-100 overflow-hidden z-50 animate-in slide-in-from-top-2 duration-200">
                {/* Header */}
                <div className="bg-gradient-to-r from-emerald-500 to-green-600 text-white p-4 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-10 animate-pulse"></div>
                  <div className="flex justify-between items-center relative z-10">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                        <ShoppingCart className="w-4 h-4" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">Shopping Cart</h3>
                        <p className="text-xs opacity-90">{totalItems} item{totalItems !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => setIsCartOpen(false)}
                      className="p-1 hover:bg-white hover:bg-opacity-20 rounded-full transition-all duration-200"
                    >
                      <X className="w-5 h-5" />
                </button>
              </div>
                </div>

                {/* Cart Content */}
                <div className="p-4 max-h-96 overflow-y-auto">
              {cartItems.length > 0 ? (
                <>
                      <div className="space-y-3">
                    {Object.values(
                      cartItems.reduce((acc, item) => {
                        if (!acc[item.id]) {
                          acc[item.id] = {
                            ...item,
                            sizes: [item.selectedSize?.name],
                          };
                        } else {
                          acc[item.id].sizes.push(item.selectedSize?.name);
                          acc[item.id].quantity += item.quantity;
                        }
                        return acc;
                      }, {})
                    ).map((item) => (
                          <div
                        key={item.id}
                            className="bg-gray-50 rounded-xl p-3 hover:bg-gray-100 transition-colors duration-200"
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-800 text-sm">{item.name}</h4>
                                <p className="text-xs text-gray-500 mt-1">
                            Size: {item.sizes.join(", ")}
                          </p>
                        </div>
                              <div className="flex items-center space-x-2 ml-3">
                                <button
                                  onClick={() =>
                                    updateQuantity(
                                      item.id,
                                      item.quantity - 1,
                                      item.selectedSize
                                    )
                                  }
                                  className="w-6 h-6 bg-red-100 hover:bg-red-200 text-red-600 rounded-full flex items-center justify-center transition-colors duration-200"
                                >
                                  <Minus className="w-3 h-3" />
                                </button>
                                <span className="w-8 text-center font-medium text-sm">{item.quantity}</span>
                          <button
                            onClick={() =>
                              updateQuantity(
                                item.id,
                                item.quantity + 1,
                                item.selectedSize
                              )
                            }
                                  className="w-6 h-6 bg-green-100 hover:bg-green-200 text-green-600 rounded-full flex items-center justify-center transition-colors duration-200"
                                >
                                  <Plus className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() =>
                              removeFromCart(item.id, item.selectedSize)
                            }
                                  className="w-6 h-6 bg-red-100 hover:bg-red-200 text-red-600 rounded-full flex items-center justify-center transition-colors duration-200"
                          >
                                  <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Checkout Button */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                    <button
                      onClick={handleCheckout}
                          className="w-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-green-600 hover:to-emerald-500 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-xl"
                    >
                          Proceed to Quote
                    </button>
                  </div>
                </>
              ) : (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <ShoppingCart className="w-8 h-8 text-gray-400" />
                      </div>
                      <h4 className="font-medium text-gray-800 mb-2">Your cart is empty</h4>
                      <p className="text-sm text-gray-500">Add some products to get started</p>
            </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </nav>
    </div>
  );
};

export default Navbar;